import React, { createContext } from 'react';
import { facets } from '../components/search/facets/facetLogic';
import log from 'loglevel';
import { EdmTypes, Field, FieldConfiguration } from '../types/Field';
import { AuthenticationContext } from './AuthenticationContext';
import { AutoCompleteRequest, DataContext, DataContextProps, SearchRequest } from './DataProviderContext';
import { DurationFacetValue, FacetValue, FacetValues, FacetValuesCount, GeographyPointsFacetValue, NumberFacetValue, ReplayAPI_FacetValue, StringFacetValue, TimeStartFacetValue } from '../types/Facet';
import moment from 'moment';

const FACET_SELECTIONS = 'facetSelections_v2'

export const SearchContext = createContext<SearchContextProviderState>({
    isExecutingSearch: false,
    isInitialized: false,
    filterableUIFields: undefined,
    facetsValues: [],
    facetSelections: [],
    fieldConfiguration: undefined,
    defaultFacetSelections: {},
    sorterConfig: [],
    searchSettings: {
        top: 50,
        skip: 0,
        query: "",
        sortBy: "TimeStart",
        sortOrder: "desc",        
        searchFields: []
    },
    searchResult: {},
    autoCompleteResult: {},    
    activeTenantId: "",
    executeAutoCompleteQuery:  (autoCompleteQuery: string, searchFields: string[]) => {throw new Error('Should be implemented by SearchContextProviderInternal')},
    triggerInitialisation: () => {throw new Error('Should be implemented by SearchContextProviderInternal')},
    triggerExecuteSearch: () => {throw new Error('Should be implemented by SearchContextProviderInternal')},
    updateSearchQuery:(searchQuery: string, searchFields: string[]) => {throw new Error('Should be implemented by SearchContextProviderInternal')},
    updateFacetSelection:(facetName: string, facetValue: FacetValue, createUpdatedSelection:any) => {throw new Error('Should be implemented by SearchContextProviderInternal')},
    updateAutoCompleteClear:() => {throw new Error('Should be implemented by SearchContextProviderInternal')},
    updateSorting: (sortBy: any, sortOrder: any) => {throw new Error('Should be implemented by SearchContextProviderInternal')},
    pageUp: () => {throw new Error('Should be implemented by SearchContextProviderInternal')},
    pageDown:() => {throw new Error('Should be implemented by SearchContextProviderInternal')},
    loadPage:(pageNumber: number) => {throw new Error('Should be implemented by SearchContextProviderInternal')},
    loadAnalytics:(isVisible: boolean) => {throw new Error('Should be implemented by SearchContextProviderInternal')},    
    clearFilters: (fieldNames: string[]) => {throw new Error('Should be implemented by SearchContextProviderInternal')},
});


const loadActiveFiltersFromLS = (fields: Field[]) : FacetValues<FacetValue>[] | null => {

    const cashedFilters = localStorage.getItem(FACET_SELECTIONS);

    if (!cashedFilters) {
        return null;
    }

    const rawFilters = JSON.parse(cashedFilters) as FacetValues<FacetValue>[];
    
    return rawFilters.map((rawFilter) => {
        const field = fields.find(f => f.name === rawFilter.facetName);
        if (!field) {
            throw new Error(`There is no field configuration for the field ${rawFilter.facetName}.`);
        }

        switch (field.systemSettings.type) {
            case EdmTypes.String:
            case EdmTypes.StringCollection:
                return {
                    facetName: rawFilter.facetName,
                    facetValues: rawFilter.facetValues.map((fv: FacetValue) => new StringFacetValue(fv.facetName, (fv as StringFacetValue).value))
                } as FacetValues<FacetValue>;
            case EdmTypes.Int32:
            case EdmTypes.Int64:
                return {
                    facetName: rawFilter.facetName,
                    facetValues: rawFilter.facetValues.map((fv: FacetValue) => new NumberFacetValue(fv.facetName, (fv as NumberFacetValue).value))
                } as FacetValues<FacetValue>;
            case EdmTypes.DateTimeOffset:
                return {
                    facetName: rawFilter.facetName,
                    facetValues: rawFilter.facetValues.map((fv: FacetValue) => {
                        const tsv = fv as TimeStartFacetValue;
                        return new TimeStartFacetValue(tsv.facetName, tsv.periodIndex, tsv.period, moment(tsv.fromDateTime), moment(tsv.toDateTime));
                    })
                } as FacetValues<TimeStartFacetValue>;
            case EdmTypes.Duration:
                return {
                    facetName: rawFilter.facetName,
                    facetValues: rawFilter.facetValues.map((fv: FacetValue) => {
                        const dfv = fv as DurationFacetValue;
                        return new DurationFacetValue(dfv.facetName, dfv.periodIndex, dfv.lowerValue, dfv.upperValue);
                    })
                } as FacetValues<FacetValue>;
            case EdmTypes.GeographyPointCollection:
                return {
                    facetName: rawFilter.facetName,
                    facetValues: rawFilter.facetValues.map((fv: FacetValue) => {
                        const gfv = fv as GeographyPointsFacetValue;
                        return new GeographyPointsFacetValue(gfv.facetName, gfv.longitude, gfv.latitude, gfv.radius);
                    })
                } as FacetValues<FacetValue>;
            default:
                throw new Error(`Unsupported data type found in local storage for field ${rawFilter.facetName}: ${field.systemSettings.type}`);
        }
    });    
}

function createFacetSelectionDefault(fields: Field[]) : FacetValues<FacetValue>[] {
    let facetSelection: FacetValues<FacetValue>[] = [];

    for (let i = 0; i < fields.length; i++) {
        const field = fields[i];

        const facetLogic: any = facets.getFacetLogicByType(field.systemSettings.type);

        if (!facetLogic) {
            log.error(`[createFacetRequestFromFacetConfig] Unsupported data type found: ${field.systemSettings.type}`);
            continue;
        }

        if (facetLogic.getDefaultSelection) {
            facetSelection.push(                
                facetLogic.getDefaultSelection(field.name)
            );            
        }
    }
    return facetSelection;
}

function createFilterFromFacetSelections(facetSelections: FacetValues<FacetValue>[], fields: Field[]) : string {
    
    let filterClauses: string[] = [];
    for (let i = 0; i < facetSelections.length; i++) {
        const currentFacetName = facetSelections[i].facetName;
        const currentFacetSelection = facetSelections[i];

        const field: Field | undefined = fields.find(f => f.name === currentFacetName);
        if(!field){
            throw new Error(`There is no field configuration for the field ${currentFacetName}.`);
        }

        const facetLogic = facets.getFacetLogicByType(field.systemSettings.type);

        if (!facetLogic) {
            log.error(`[createFilterFromFacetSelections] Unsupported data type found: ${field.systemSettings.type}`);
            continue;
        }        

        filterClauses.push(facetLogic.createFilter(currentFacetSelection, field));
    }

    // Remove all empty entries from the filter clauses.
    filterClauses = filterClauses.filter(entry => entry !== "");
    if (filterClauses.length === 0) {
        return "";
    }

    // Create one filter clause that contains all the filters from the facets
    // by combining them with the 'and' operator.
    const filterClausesString = filterClauses.join(' and ');
    return filterClausesString;
}

function createFacetRequestFromFacetConfig(fields: Field[]) {
    let facetableFields = fields.filter(f => f.systemSettings.isFacetable);

    let facetRequest: any[] = [];
    for (let i = 0; i < facetableFields.length; i++) {
        const field = facetableFields[i];

        const facetLogic = facets.getFacetLogicByType(field.systemSettings.type);

        if (!facetLogic) {
            log.error(`[createFacetRequestFromFacetConfig] Unsupported data type found: ${field.systemSettings.type}`);
            continue;
        }
        
        const request = facetLogic.createRequest(field);
        if(request && request !== "" ){ //Some fields. like Locations, shown in the side-bar are not facet-able and can not be requested. Those field  return undefined of empty string.
            facetRequest.push(request);
        }
    }

    return facetRequest;
}

function extractFacetValues(facetValuesFromSearch: Record<string, ReplayAPI_FacetValue>, fields: Field[]) : FacetValuesCount[] {        
    const facetNames = Object.keys(facetValuesFromSearch);

    let extractedFacetValues:FacetValuesCount[] = [];

    for (let i = 0; i < facetNames.length; i++) {
        const currentFacetName = facetNames[i];
        const currentFacetValues = facetValuesFromSearch[currentFacetName];
        const field: Field | undefined = fields.find(f => f.name === currentFacetName);

        if(!field){
            throw new Error(`There is no field configuration for the field ${currentFacetName}.`);
        }
        const facetLogic = facets.getFacetLogicByType(field.systemSettings.type)

        if (!facetLogic) {
            log.error(`[extractFacetValues] Unexpected facet type found: ${field.systemSettings.type}`);
            continue;
        }
        
        extractedFacetValues.push({
            facetName: currentFacetName,
            facetValues: facetLogic.extractFacetValues(currentFacetName, currentFacetValues)
        });
    }

    return extractedFacetValues;
}

interface SearchSettings{
    top: number;
    skip: number;
    query: string;
    sortBy: string;
    sortOrder: string;
    searchFields: string[];
}

interface SearchContextProviderProps {
    dataContext: DataContextProps;
    children?: React.ReactNode
}


export interface SearchContextProviderState {
    isInitialized: boolean;
    isExecutingSearch: boolean;
    filterableUIFields?: Field[];
    fieldConfiguration?: FieldConfiguration;        
    facetsValues: FacetValuesCount[]; 
    facetSelections: FacetValues<FacetValue>[];
    defaultFacetSelections: any;
    sorterConfig: any;
    searchSettings: SearchSettings;
    searchResult: any;
    autoCompleteResult: any;    
    activeTenantId: string,
    triggerInitialisation() : void;
    triggerExecuteSearch() : void;
    updateSearchQuery(searchQuery: string, searchFields?: string[]): void;
    executeAutoCompleteQuery(autoCompleteQuery: string, searchFields: string[]): Promise<void>;
    updateFacetSelection(facetName: string, facetValue: FacetValue, createUpdatedSelection:any): void;
    updateAutoCompleteClear(): void;
    updateSorting(sortBy: any, sortOrder: any): void;
    pageUp(): void;
    pageDown(): void;
    loadPage(pageNumber: number): void;
    loadAnalytics(isVisible: boolean): void;    
    clearFilters(fieldNames: string[]): void;
}

class SearchContextProviderInternal extends React.Component<SearchContextProviderProps, SearchContextProviderState> {
    context!: React.ContextType<typeof AuthenticationContext>;
    static contextType = AuthenticationContext;

    constructor(props: SearchContextProviderProps) {        
        super(props);        

        const cacheState = localStorage.getItem('cacheTopBarFilter')
        const cached =  cacheState ? JSON.parse(cacheState) : '';

        this.state = {
            isExecutingSearch: false,
            isInitialized: false,
            filterableUIFields: undefined,                        
            facetsValues: [],
            facetSelections: [],
            fieldConfiguration: undefined,
            defaultFacetSelections: {},
            sorterConfig: [],
            searchSettings: {
                top: 50,
                skip: 0,
                query: cached.searchQuery || "",
                sortBy: "TimeStart",
                sortOrder: "desc",
                searchFields: cached.searchFields || []
            },
            searchResult: {},
            autoCompleteResult: {},            
            activeTenantId: "",
            triggerInitialisation: async () => {
                if (!this.shouldInitialize()) {
                    return;
                }
            
                let fieldConfiguration = await this.props.dataContext.getFields();
                fieldConfiguration.fields = fieldConfiguration.fields.filter(f => f.name !== 'DeleteAfter');
                const filterableUIFields:Field[] = fieldConfiguration.fields
                    .filter(f => f.systemSettings.isFilterable && f.userSettings.showInFacetArea)
                    .sort((f1, f2) => {return f1.userSettings.orderInFacetArea > f2.userSettings.orderInFacetArea ? 1 : -1});

                const sorterResponse = await this.props.dataContext.getSorters();

                const cashedActiveFilters = loadActiveFiltersFromLS(filterableUIFields)

                const facetSelectionDefault = createFacetSelectionDefault(filterableUIFields);

                const initialSearchResult = await this.executeSearch(!!cashedActiveFilters ? cashedActiveFilters : facetSelectionDefault, filterableUIFields);
                
                const facetValues = extractFacetValues(initialSearchResult.facets, filterableUIFields);                                        
                
                this.setState({
                    isInitialized: true,
                    filterableUIFields: filterableUIFields,
                    sorterConfig: sorterResponse.sorters,
                    searchResult: initialSearchResult,
                    facetsValues: facetValues,
                    facetSelections: cashedActiveFilters ?? facetSelectionDefault,
                    defaultFacetSelections: facetSelectionDefault,
                    fieldConfiguration: fieldConfiguration,
                    activeTenantId: this.context?.activeTenantId ?? ""
                });
            
            },
            triggerExecuteSearch: async () => {
                                
                if(!this.state.filterableUIFields){
                    log.error(`triggerExecuteSearch executed before field configuration was available in the state.`);
                    return ;
                }

                const cashedActiveFilters = loadActiveFiltersFromLS(this.state.filterableUIFields);

                const searchResult = await this.executeSearch(!!cashedActiveFilters ? cashedActiveFilters : this.state.facetSelections, this.state.filterableUIFields);
                const facetValues = extractFacetValues(searchResult.facets, this.state.filterableUIFields);

                this.setState({                        
                    searchResult: searchResult,
                    facetsValues: facetValues,
                });
            
            },
            updateSearchQuery: (searchQuery: string, searchFields?: string[]) => {
                this.setState(prevState => {
                    return {
                        ...prevState,                        
                        searchSettings: {
                            ...prevState.searchSettings,
                            skip: 0,
                            query: searchQuery,
                            searchFields: searchFields === undefined ? prevState.searchSettings.searchFields : searchFields
                        },
                    }
                }, this.state.triggerExecuteSearch);
            },
            executeAutoCompleteQuery: async (autoCompleteQuery: string, searchFields: string[]) => {
                if (!autoCompleteQuery || autoCompleteQuery.length === 0) {
                    this.setState({
                        autoCompleteResult: []
                    });

                    return
                }

                const autoCompleteSettings: AutoCompleteRequest=  {
                    top: 5,
                    query: autoCompleteQuery,
                    searchFields: searchFields
                }

                var autoCompleteResult = await this.props.dataContext.autoComplete(autoCompleteSettings);

                this.setState({
                    autoCompleteResult: autoCompleteResult
                })
            },
            updateAutoCompleteClear: () => {
                this.setState({
                    autoCompleteResult: ""
                });
            },
            clearFilters: (fieldNames: string[]) => {                
                this.setState((prevState) => {
                    let newFacetSelection = prevState.facetSelections.filter((fs) => !fieldNames.includes(fs.facetName));
                    if (newFacetSelection.length === 0) {
                        newFacetSelection = createFacetSelectionDefault(prevState.filterableUIFields || []);
                    }

                    return {
                        ...prevState,
                        facetSelections: newFacetSelection,
                        searchSettings: {
                            ...prevState.searchSettings,
                            skip: 0
                        }
                    };
                }, () => {
                    localStorage.removeItem(FACET_SELECTIONS);
                    this.state.triggerExecuteSearch();
                });
            },
            updateFacetSelection: (facetName: string, facetValue: FacetValue, createUpdatedSelection: any) => {
                if (this.state.isExecutingSearch) {
                    return;
                }              

                this.setState(prevState => {
                    const currentSelectionOfFacet = 
                        prevState.facetSelections?.find((f: FacetValues<FacetValue>) => f.facetName === facetName)|| 
                        {
                            facetName: facetName,
                            facetValues: [] as FacetValue[]
                        };

                    const updatedFacetSelection = createUpdatedSelection(facetValue, currentSelectionOfFacet)

                    if (!currentSelectionOfFacet) { // If there was no selection before for the facet, we need to set the facet name, because the createUpdatedSelection function does not set it (because it doesn't know the facet name).
                        updatedFacetSelection.facetName = facetName;
                    }

                    let newFacetsSelections;
                    const existingFacet = prevState.facetSelections.find((fs) => fs.facetName === facetName);
                    if (existingFacet) {
                        newFacetsSelections = prevState.facetSelections.map((fs) => {
                            if (fs.facetName === facetName) {
                                return updatedFacetSelection;
                            }
                            return fs;
                        });
                    } else {
                        newFacetsSelections = [...prevState.facetSelections, updatedFacetSelection];
                    }

                    return {
                        ...prevState,
                        facetSelections: newFacetsSelections,
                        searchSettings: {
                            ...prevState.searchSettings,
                            skip: 0
                        }
                    }
                }, () => {
                    localStorage.setItem(FACET_SELECTIONS, JSON.stringify(this.state.facetSelections))
                    this.state.triggerExecuteSearch()
                });
            },
            updateSorting: (sortBy: any, sortOrder: any) => {
                this.setState(prevState => {
                    return {
                        ...prevState,
                        searchSettings: {
                            ...prevState.searchSettings,
                            skip: 0,
                            sortBy: sortBy,
                            sortOrder: sortOrder,
                        }
                    }
                }, this.state.triggerExecuteSearch);
            },
            pageUp: () => {
                this.setState(prevState => {
                    const newSkipValue = prevState.searchSettings.skip + prevState.searchSettings.top;

                    return {
                        ...prevState,
                        searchSettings: {
                            ...prevState.searchSettings,
                            skip: newSkipValue
                        }
                    }
                }, this.state.triggerExecuteSearch);
            },
            pageDown: () => {
                this.setState(prevState => {
                    const newSkipValue = prevState.searchSettings.skip - prevState.searchSettings.top;

                    return {
                        ...prevState,
                        searchSettings: {
                            ...prevState.searchSettings,
                            skip: newSkipValue
                        }
                    }
                }, this.state.triggerExecuteSearch);
            },
            loadPage: (pageNumber: number) => {
                this.setState(prevState => {
                    const zeroBasedPageNumber = pageNumber - 1;
                    const newSkipValue = zeroBasedPageNumber * prevState.searchSettings.top;

                    return {
                        ...prevState,
                        searchSettings: {
                            ...prevState.searchSettings,
                            skip: newSkipValue
                        }
                    }
                }, this.state.triggerExecuteSearch);
            },
            loadAnalytics: (isVisible: boolean) => {
                this.setState(prevState => {
                    return {
                        ...prevState
                    }
                }, this.state.triggerExecuteSearch);
            }
        }
    }
    
    shouldInitialize = () : boolean => {
        if (this.state.activeTenantId !== this.context?.activeTenantId){
            return true;
        }

        return !this.state.isInitialized;
    }

    executeSearch = async (facetSelections: FacetValues<FacetValue>[], fields: Field[]) => {
        this.setState({
            isExecutingSearch: true
        });

        try {
            let searchSettings = this.state.searchSettings;

            let facetNames = createFacetRequestFromFacetConfig(fields);
            let filterClause = createFilterFromFacetSelections(facetSelections, fields);

            let query: string = "*";
            if (searchSettings.query.startsWith("$filter:")){
                query = "";
                let queryFilter = searchSettings.query.substr(8);
                log.warn("Using filter query in search bar. Is an experimental feature. Filter:", queryFilter)
                if(filterClause === ""){
                    filterClause = queryFilter;
                }
                else{
                    filterClause = "(" + filterClause + ") and (" + queryFilter + ")";
                }
            }
            else if (searchSettings.query!=="") {
                query = searchSettings.query;
            }

            // Select only fields that are needed by the frontend
            const fieldsToSelect = [
                "MediaId",
                "Owner/DisplayName",
                "Owner/ParticipantId",
                "Owner/PhoneNumber",
                "Participants/DisplayName",
                "Participants/ParticipantId",
                "Participants/PhoneNumber",
                "Direction",
                "TimeStart",
                "Duration",
                "SourceType",
                "MediaTypes",
                "ContentReferences"
            ];

            let searchRequest:SearchRequest = {
                facets: facetNames,
                filter: filterClause,
                top: searchSettings.top,
                skip: searchSettings.skip,
                orderBy: [`${searchSettings.sortBy} ${searchSettings.sortOrder}`],
                searchFields: searchSettings.searchFields,
                includeTotalResultCount: true,
                query: query,
                searchMode: "All",
                select: fieldsToSelect                
            };

            return await this.props.dataContext.search(searchRequest);
        }
        finally {
            this.setState({
                isExecutingSearch: false
            });
        }
    }

    render() {        
        return (
            <SearchContext.Provider value={this.state}>
                {this.props.children}
            </SearchContext.Provider>
        )
    }
}

const SearchContextProvider = (Component: React.ComponentType<any>) => {
    return function DataContextComponent(props: any) {
      return (
        <DataContext.Consumer>
          {(dataContextValue) =>
            (
              <SearchContextProviderInternal {...props} dataContext={dataContextValue} />
            ) 
          }
        </DataContext.Consumer>
      );
    };
  };
  
  export default SearchContextProvider(SearchContextProviderInternal);
  